import React, { Component } from 'react';

import { translate } from '../../base/i18n/functions';

/**
 * The type of the React {@code Component} props of {@link DeviceSelector}.
 */
type Props = {

  /**
   * MediaDeviceInfos used for display in the select element.
   */
  devices: Array<Object>,

  /**
   * If false, will return a selector with no selection options.
   */
  hasPermission: boolean,

  /**
   * CSS class for the icon to the left of the dropdown trigger.
   */
  icon: string,

  /**
   * If true, will render the selector disabled with a default selection.
   */
  isDisabled: boolean,

  /**
   * The translation key to display as a menu label.
   */
  label: string,

  /**
   * The callback to invoke when a selection is made.
   */
  onSelect: Function,

  /**
   * The default device to display as selected.
   */
  selectedDeviceId: string,

  /**
   * Invoked to obtain translated strings.
   */
  t: Function,
};

/**
 * The type of the React {@code Component} state of {@link DeviceSelector}.
 */
type State = {

  /**
   * Whether or not the dropdown is currently open.
   */
  isOpen: boolean,
};

/**
 * React component for selecting a device from a select element. Wraps
 * a plain HTML/React dropdown with device selection specific logic.
 *
 * @augments Component
 */
class DeviceSelector extends Component<Props, State> {
    /**
   * Initializes a new DeviceSelector instance.
   *
   * @param {Object} props - The read-only React Component props with which
   * the new instance is to be initialized.
   */
    constructor(props) {
        super(props);

        this.state = {
            isOpen: false
        };

        this._onSelect = this._onSelect.bind(this);
        this._createDropdownItem = this._createDropdownItem.bind(this);
        this._onToggleOpen = this._onToggleOpen.bind(this);
        this._closeDropdown = this._closeDropdown.bind(this);
        this._containerRef = React.createRef();
        this._onDocumentClick = this._onDocumentClick.bind(this);
    }

    /**
   * Adds a click listener to close the dropdown when clicking outside of it.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentDidMount() {
        document.addEventListener('mousedown', this._onDocumentClick);
    }

    /**
   * Removes the click listener.
   *
   * @inheritdoc
   * @returns {void}
   */
    componentWillUnmount() {
        document.removeEventListener('mousedown', this._onDocumentClick);
    }

    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        if (!this.props.hasPermission) {
            return this._renderNoPermission();
        }

        if (!this.props.devices || !this.props.devices.length) {
            return this._renderNoDevices();
        }

        const items = this.props.devices.map(this._createDropdownItem);
        let defaultSelected = this.props.devices.find(
            item => item.deviceId === this.props.selectedDeviceId
        );

        if (!defaultSelected && this.props.devices.length > 0) {
            defaultSelected = this.props.devices.find(item => item.deviceId === 'default')
                || this.props.devices[0];
        }

        return this._createDropdown({
            defaultSelected,
            isDisabled: this.props.isDisabled,
            items,
            placeholder: this.props.t('deviceSelection.selectADevice')
        });
    }

    /**
   * Creates a React Element for displaying the passed in text surrounded by
   * two icons. The left icon is the icon class passed in through props and
   * the right icon is an expand indicator.
   *
   * @param {string} triggerText - The text to display within the element.
   * @private
   * @returns {ReactElement}
   */
    _createDropdownTrigger(triggerText) {
        return (
            <div
                className = 'device-selector-trigger'
                onClick = { this._onToggleOpen }>
                <span className = 'device-selector-trigger-text'>{triggerText}</span>
            </div>
        );
    }

    _createDropdownItem: (Object) => void;

    /**
   * Creates a React Element for an individual selectable option.
   *
   * @param {MediaDeviceInfo} device - An object with a label and a deviceId.
   * @private
   * @returns {ReactElement} The passed in media device description converted
   * to a selectable list item.
   */
    _createDropdownItem(device) {
        return (
            <li
                className = 'device-selector-item'
                key = { device.deviceId }
                onClick = { e => {
                    e.stopPropagation();
                    this._onSelect(device.deviceId);
                } }
                role = 'option'>
                {device.label || device.deviceId}
            </li>
        );
    }

    /**
   * Creates a dropdown Component using passed in props and options. If
   * the dropdown needs to be disabled, then only the trigger element is
   * returned to simulate a disabled state.
   *
   * @param {Object} options - Additional configuration for display.
   * @param {Object} options.defaultSelected - The option that should be set
   * as currently chosen.
   * @param {boolean} options.isDisabled - If true, only the trigger
   * component will be returned to simulate a disabled dropdown.
   * @param {Array} options.items - All the selectable options to display.
   * @param {string} options.placeholder - The translation key to display when
   * no selection has been made.
   * @private
   * @returns {ReactElement}
   */
    _createDropdown(options) {
        const triggerText
      = (options.defaultSelected
        && (options.defaultSelected.label || options.defaultSelected.deviceId))
      || options.placeholder;
        const trigger = this._createDropdownTrigger(triggerText);

        if (options.isDisabled || !options.items.length) {
            return <div className = 'device-selector-trigger-disabled'>{trigger}</div>;
        }

        return (
            <div
                className = 'dropdown-menu1'
                ref = { this._containerRef }>
                {trigger}
                { this.state.isOpen && (
                    <ul className = 'device-selector-list'>
                        {options.items}
                    </ul>
                ) }
            </div>
        );
    }

    _onSelect: (Object) => void;

    /**
   * Invokes the passed in callback to notify of selection changes.
   *
   * @param {Object} newDeviceId - Selected device id from the dropdown option.
   * @private
   * @returns {void}
   */
    _onSelect(newDeviceId) {
        if (this.props.selectedDeviceId !== newDeviceId) {
            this.props.onSelect(newDeviceId);
        }
        this._closeDropdown();
    }

    /**
   * Toggles the open state of the dropdown.
   *
   * @private
   * @returns {void}
   */
    _onToggleOpen(event) {
        event.stopPropagation();
        this.setState(prevState => {
            return { isOpen: !prevState.isOpen };
        });
    }


    /**
   * Closes the dropdown.
   *
   * @private
   * @returns {void}
   */
    _closeDropdown() {
        this.setState({ isOpen: false });
    }

    /**
   * Closes the dropdown if a click occurs outside of it.
   *
   * @param {MouseEvent} event - The click event.
   * @private
   * @returns {void}
   */
    _onDocumentClick(event) {
        if (this.state.isOpen
      && this._containerRef.current
      && !this._containerRef.current.contains(event.target)) {
            this._closeDropdown();
        }
    }

    /**
   * Creates a disabled dropdown with a placeholder indicating there are no
   * devices to select.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderNoDevices() {
        return this._createDropdown({
            isDisabled: true,
            placeholder: this.props.t('settings.noDevice')
        });
    }

    /**
   * Creates a disabled dropdown with a placeholder stating there is no
   * permission to display the devices.
   *
   * @private
   * @returns {ReactElement}
   */
    _renderNoPermission() {
        return this._createDropdown({
            isDisabled: true,
            placeholder: this.props.t('deviceSelection.noPermission')
        });
    }
}

export default translate(DeviceSelector);
